import { useMutation, useQueryClient } from "@tanstack/react-query";
import { communityApi } from "@bw/shared/api";
import {
  getFeedIdentityGeneration,
  mergeConfirmedFeedPost,
  scheduleFeedReconciliation,
} from "@bw/shared/hooks";
import type { PostResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";
import { INLINE_GUARDIAN_PROMPT } from "../../../common/guardian/denials";

/** The variables a single repost carries: which post to repost, an OPTIONAL quote
 * (commentary shown above the embedded original), and an OPTIONAL `communityId`.
 * An empty/blank quote is a pure repost — the hook passes `undefined` so no body
 * quote is sent. When `communityId` is set the repost lands in THAT community's
 * feed (the share sheet's "Share to a community" affordance); when absent it
 * reposts into the caller's OWN friends feed (the share sheet's "Repost" /
 * "Repost with your thoughts"). */
export interface RepostVars {
  postId: string;
  quote?: string;
  communityId?: string;
}

/**
 * Repost (S1b, WRITE). NON-OPTIMISTIC: nothing changes before confirmation. On
 * success the exact returned repost is merged into eligible canonical Feed caches;
 * affected projection/count state is then reconciled with bounded server reads.
 *
 * Repost can be fired from any Feed surface or post detail. The existing social
 * detail arm is invalidated for source aggregates, while the canonical Feed family
 * uses its own shared keys and projection-aware reconciliation.
 *
 * Identity is the token user server-side; the FE sends only the post id + the
 * optional quote (trimmed by the caller). Re-entrancy is the page's concern: it
 * disables the submit button while `isPending` so a double-submit fires once. A
 * rejected repost (409 already-reposted / 403 blocked / network) leaves the feed
 * unchanged and surfaces on `mutation.error` for the page to show honestly via
 * role="alert".
 */
export function useCreateRepost() {
  const queryClient = useQueryClient();

  return useMutation<PostResponse, unknown, RepostVars, number>({
    onMutate: () => getFeedIdentityGeneration(queryClient),
    // Preserve the original 2-arg call when there is no target community, so the
    // pure repost-to-own-feed path is byte-identical (no trailing `undefined`
    // third arg) — only a community share passes the 3rd argument.
    meta: INLINE_GUARDIAN_PROMPT,
    mutationFn: ({ postId, quote, communityId }: RepostVars) =>
      communityId != null && communityId !== ""
        ? communityApi.createRepost(postId, quote, communityId)
        : communityApi.createRepost(postId, quote),
    onSuccess: (post, _variables, generation) => {
      if (generation !== getFeedIdentityGeneration(queryClient)) return;
      // Source-detail aggregates may live in the social detail arm. Feed cards
      // use the separate canonical Feed cache below.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.social.all,
      });
      const immediateTargets = post.community_id
        ? [{ scope: "community" as const, subject: post.community_id }]
        : [
            { scope: "home" as const },
            { scope: "friends" as const },
            { scope: "user" as const, subject: post.author_id },
          ];
      mergeConfirmedFeedPost(queryClient, post, immediateTargets);
      scheduleFeedReconciliation(queryClient, [
        ...immediateTargets,
        ...(post.community_id
          ? [
              { scope: "home" as const },
              { scope: "communities" as const },
              { scope: "user" as const, subject: post.author_id },
            ]
          : []),
      ]);
    },
  });
}
